import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Activity, ArrowRight, Loader2, Save, ShieldAlert, Sliders, TrendingDown, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ApifyKillSwitchPanel from "@/components/ApifyKillSwitchPanel";

const clamp = (v, min, max) => Math.max(min, Math.min(max, Number(v) || 0));

export default function RadarSettings() {
  const [account, setAccount] = useState(null);
  const [pct, setPct] = useState(20);        // 0..90 (display %)
  const [credMin, setCredMin] = useState(35); // 0..90 (display %)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  // Live preview state
  const [preview, setPreview] = useState(null);       // { subvaluados, sospechosos, actual_subvaluados, delta, total }
  const [previewing, setPreviewing] = useState(false);
  const previewTimer = useRef(null);
  const previewSeq = useRef(0);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/accounts/me");
      const s = r.data.settings || {};
      setAccount(r.data);
      setPct(Math.round((s.subvaluado_pct ?? 0.2) * 100));
      setCredMin(Math.round((s.credibilidad_min_ratio ?? 0.35) * 100));
    } catch (e) {
      toast.error("No se pudo cargar la cuenta");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Debounced live preview — fires 300ms after the last slider change
  useEffect(() => {
    if (loading || !account) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      const seq = ++previewSeq.current;
      setPreviewing(true);
      try {
        const r = await api.post("/accounts/me/settings/preview", {
          subvaluado_pct: clamp(pct, 1, 90) / 100,
          credibilidad_min_ratio: clamp(credMin, 0, 90) / 100,
        });
        if (seq === previewSeq.current) setPreview(r.data);
      } catch {
        // silent — preview is best-effort
      } finally {
        if (seq === previewSeq.current) setPreviewing(false);
      }
    }, 300);
    return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pct, credMin, loading, account?.account_id]);

  const save = async () => {
    setSaving(true);
    setLastResult(null);
    try {
      const r = await api.patch("/accounts/me/settings", {
        subvaluado_pct: clamp(pct, 1, 90) / 100,
        credibilidad_min_ratio: clamp(credMin, 0, 90) / 100,
      });
      setAccount(r.data.account);
      setLastResult(r.data.recompute);
      toast.success(
        `${r.data.recompute.actualizados} oportunidades recalculadas · ` +
        `${r.data.recompute.subvaluados} subvaluadas · ` +
        `${r.data.recompute.sospechosos} sospechosas`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="p-12 text-slate-500 flex items-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Cargando ajustes…
    </div>
  );

  const ratioMax = (100 - pct) / 100;

  return (
    <div className="p-8 lg:p-12 max-w-4xl">
      <header className="mb-10">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-3">Ajustes</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          Criterio de oportunidad
        </h1>
        <p className="font-body text-base text-slate-600 mt-2 max-w-2xl">
          Definí el umbral que usa el radar para marcar una publicación como oportunidad.
          El precio de referencia se calcula por <strong>zona × tipo × operación × moneda</strong> —
          nunca mezclando peras con manzanas.
        </p>
      </header>

      {/* Kill-switch de Apify — PRIMERO Y PROMINENTE */}
      <ApifyKillSwitchPanel />

      {/* Acceso a Actividad — control de costos (no en el menú principal, sí acá).
          Ver Layout.jsx: sacamos "Actividad" del top-nav en la limpieza cosmética
          pre-Agustín pero el registro de gasto y auditoría siguen accesibles desde
          Ajustes porque son control de costos. */}
      <section className="bg-white border border-slate-200 p-6 mb-6" data-testid="settings-actividad-link">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-4 h-4 text-slate-700" />
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700">Actividad y gasto</p>
            </div>
            <p className="text-sm text-slate-600">Historial de capturas, corridas Apify, costos y auditoría.</p>
          </div>
          <Link
            to="/radar/actividad"
            data-testid="link-actividad"
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 hover:border-slate-900 text-sm font-medium text-slate-800 hover:text-slate-950 transition-colors"
          >
            Ver actividad <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

      {/* Global % threshold */}
      <section className="bg-white border border-slate-200 p-6 mb-6" data-testid="settings-pct-panel">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="w-4 h-4 text-emerald-700" />
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-800">
                Umbral global
              </p>
            </div>
            <h2 className="font-heading text-xl font-bold text-slate-900">
              Marcar como oportunidad si está{" "}
              <span className="text-emerald-700" data-testid="settings-pct-inline">{pct}%</span>{" "}
              bajo la mediana de la zona
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Ej: mediana US$ 1.000/m² · umbral {pct}% → oportunidad si precio/m² ≤ US$ {Math.round(1000 * ratioMax)}
            </p>
          </div>
          <span className="font-mono text-2xl font-bold text-slate-900 shrink-0">
            {pct}<span className="text-sm text-slate-500">%</span>
          </span>
        </div>

        <input
          type="range"
          min={5}
          max={60}
          step={1}
          value={pct}
          data-testid="settings-pct-slider"
          onChange={(e) => setPct(Number(e.target.value))}
          className="w-full accent-emerald-700"
        />
        <div className="flex justify-between text-[10px] text-slate-400 mt-1 font-mono">
          <span>5%</span><span>15%</span><span>25%</span><span>35%</span><span>45%</span><span>60%</span>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
          {[
            { v: 10, label: "Conservador" },
            { v: 20, label: "Balanceado" },
            { v: 30, label: "Agresivo" },
          ].map(({ v, label }) => (
            <button
              key={v}
              type="button"
              data-testid={`settings-pct-preset-${v}`}
              onClick={() => setPct(v)}
              className={`border py-2.5 px-3 font-heading uppercase tracking-wider transition-colors text-center ${
                pct === v ? "bg-emerald-700 text-white border-emerald-800" : "bg-white border-slate-300 text-slate-700 hover:border-emerald-500"
              }`}
            >
              <span className="block font-bold text-sm">{v}%</span>
              <span className="block text-[10px] opacity-80 mt-0.5">{label}</span>
            </button>
          ))}
        </div>

        {/* Live preview strip */}
        <PreviewStrip preview={preview} previewing={previewing} pct={pct} />
      </section>

      {/* Credibility floor */}
      <section className="bg-white border border-slate-200 p-6 mb-6" data-testid="settings-cred-panel">
        <div className="flex items-start gap-2 mb-3">
          <ShieldAlert className="w-4 h-4 text-amber-700 mt-0.5" />
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-800 mb-1">
              Piso de credibilidad
            </p>
            <h2 className="font-heading text-lg font-bold text-slate-900">
              Si el precio está más del <span data-testid="settings-cred-inline">{100 - credMin}%</span> por debajo de la mediana, marcar como <em>sospechoso</em>
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Filtro anti dato-roto. Cambia sólo si sabés lo que estás haciendo.
            </p>
          </div>
        </div>
        <div className="flex items-baseline gap-4 mt-3">
          <input
            type="range"
            min={0}
            max={80}
            step={5}
            value={credMin}
            data-testid="settings-cred-slider"
            onChange={(e) => setCredMin(Number(e.target.value))}
            className="flex-1 accent-amber-600"
          />
          <span className="font-mono text-sm font-bold text-slate-900 min-w-[60px] text-right">
            ratio ≥ {(credMin / 100).toFixed(2)}
          </span>
        </div>
      </section>

      {/* How does the reference work */}
      <section className="bg-slate-50 border border-slate-200 p-6 mb-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-3 flex items-center gap-2">
          <Sliders className="w-3.5 h-3.5" /> Cómo se calcula la referencia
        </p>
        <ul className="text-sm text-slate-700 space-y-2">
          <li>· El precio/m² de referencia es la <strong>mediana</strong> del grupo (zona, tipo, operación, moneda).</li>
          <li>· Se necesitan al menos 5 avisos comparables para que la mediana sea confiable.</li>
          <li>· Nunca se mezcla venta con alquiler, ni terrenos con departamentos, ni USD con ARS.</li>
          <li>· En cada tarjeta/ficha vas a ver la etiqueta: <em>Referencia: departamentos en venta en Alberdi · US$ 1.480/m²</em></li>
          <li>· Podés setear un <strong>umbral absoluto por zona</strong> (US$/m²) al editar la zona. Cuando existe, prevalece sobre el % para esa zona.</li>
        </ul>
      </section>

      {/* Save */}
      <div className="flex items-center gap-4">
        <Button
          data-testid="settings-save"
          onClick={save}
          disabled={saving}
          className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-none h-11 px-6"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar y recalcular
        </Button>
        {lastResult && (
          <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="settings-result">
            ✓ {lastResult.actualizados} recalculadas · {lastResult.subvaluados} subvaluadas · {lastResult.sospechosos} sospechosas
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewStrip({ preview, previewing, pct }) {
  const has = preview && preview.total > 0;
  const delta = has ? preview.delta : null;
  const deltaCls = delta == null
    ? "text-slate-500"
    : delta > 0 ? "text-emerald-700"
    : delta < 0 ? "text-amber-700"
    : "text-slate-500";
  return (
    <div
      className="mt-4 flex flex-wrap items-center gap-3 px-3 py-2.5 bg-slate-50 border-l-2 border-emerald-600"
      data-testid="settings-preview-strip"
    >
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
        {previewing
          ? <Loader2 className="w-3.5 h-3.5 text-emerald-700 animate-spin" />
          : <Wand2 className="w-3.5 h-3.5 text-emerald-700" />}
        Preview
      </div>
      {has ? (
        <>
          <span className="text-sm text-slate-700">
            Con este umbral (<span className="font-heading font-bold text-slate-900">{pct}%</span>)
            marcarías{" "}
            <span
              className="font-heading text-lg font-bold text-emerald-800"
              data-testid="settings-preview-count"
            >{preview.subvaluados}</span>{" "}
            oportunidades
          </span>
          <span className={`font-heading font-bold text-sm ${deltaCls}`} data-testid="settings-preview-delta">
            {delta === 0 ? "sin cambios" : `${delta > 0 ? "+" : ""}${delta} vs actual`}
          </span>
          <span className="text-[11px] text-slate-500 ml-auto">
            {preview.sospechosos} sospechosas · sobre {preview.total} avisos
          </span>
        </>
      ) : (
        <span className="text-xs text-slate-500 italic">Calculando…</span>
      )}
    </div>
  );
}

